import { createRouter, createWebHistory } from 'vue-router';
import Login from '../views/Login.vue'
import api from '../services/api';

const routes = [
  {path:'/plugin_store',name:'PluginStoreManage',component:()=>import('../views/PluginStoreManage.vue'),meta:{requiresAuth:true,title:'插件商店',roles:['super_admin']}},
  {path:'/store_orders',redirect:'/dashboard'},
  {path:'/viewer_cloud_accounts',name:'ViewerCloudAccounts',component:()=>import('../views/ViewerCloudAccounts.vue'),meta:{requiresAuth:true,title:'学员云空间',roles:['super_admin','teacher_admin']}},
  {path:'/runtime',name:'RuntimeStatus',component:()=>import('../views/RuntimeStatus.vue'),meta:{requiresAuth:true,title:'运行状态',roles:['super_admin']}},
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: {
      requiresAuth: true,
      title: '工作台'
    }
  },
  {
    path: '/organization',
    name: 'Organization',
    component: () => import('../views/Organization.vue'),
    meta: {
      requiresAuth: true,
      title: '组织与学员',
      roles: ['super_admin', 'teacher_admin']
    }
  },
  {
    path: '/new_auths',
    name: 'NewAuthList',
    component: () => import('../views/new_auths/List.vue'),
    meta: {
      requiresAuth: true,
      title: '学员授权'
    }
  },
  {
    path: '/new_auths/:id/edit',
    name: 'NewAuthEdit',
    component: () => import('../views/new_auths/Form.vue'),
    meta: {
      requiresAuth: true,
      title: '编辑工具授权'
    }
  },
  {
    path: '/security_audit',
    name: 'SecurityAudit',
    component: () => import('../views/security_audit/List.vue'),
    meta: {
      requiresAuth: true,
      title: '安全审计',
      roles: ['super_admin']
    }
  },
  {
    path: '/login_records',
    name: 'LoginRecordsList',
    redirect: to => ({
      name: 'SecurityAudit',
      query: { ...to.query, category: 'authentication' }
    })
  },
  {
    path: '/auths',
    name: 'AuthList',
    component: () => import('../views/auths/List.vue'),
    meta: {
      requiresAuth: true,
      title: '授权管理'
    }
  },
  {
    path: '/auths/new',
    name: 'AuthCreate',
    component: () => import('../views/auths/Form.vue'),
    meta: {
      requiresAuth: true,
      title: '新建授权'
    }
  },
  {
    path: '/auths/:id/edit',
    name: 'AuthEdit',
    component: () => import('../views/auths/Form.vue'),
    meta: {
      requiresAuth: true,
      title: '编辑授权'
    }
  },
  {
    path: '/check_records',
    name: 'CheckRecordsList',
    redirect: { name: 'SecurityAudit' }
  },
  {
    path: '/verify_records',
    name: 'VerifyRecordsList',
    redirect: { name: 'SecurityAudit' }
  },
  // 👇 发布中心 👇
  {
    path: '/publish_manage',
    name: 'PublishManage',
    component: () => import('../views/PublishManage.vue'),
    meta: {
      requiresAuth: true,
      title: '发布中心',
      roles: ['super_admin']
    }
  },
  // 👇 激活码管理 👇
  {
    path: '/activation_codes',
    name: 'ActivationCodesList',
    component: () => import('../views/activation_codes/List.vue'),
    meta: {
      requiresAuth: true,
      title: '激活码管理'
    }
  },
  {
    path: '/viewer_deliveries',
    name: 'ViewerDeliveries',
    component: () => import('../views/ViewerDeliveries.vue'),
    meta: {
      requiresAuth: true,
      title: '看图交付',
      roles: ['super_admin', 'teacher_admin']
    }
  },
  {
    path: '/backup',
    name: 'BackupIndex',
    component: () => import('../views/backup/Index.vue'),
    meta: {
      requiresAuth: true,
      title: '数据备份',
      roles: ['super_admin']
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      requiresAuth: false,
      title: '登录'
    }
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

const normalizeRole = (role) => role === 'admin' ? 'super_admin' : role;

const getStoredRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return normalizeRole(user.role);
  } catch (_) {
    return '';
  }
};

const hasStoredUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return Boolean(user.id || user.username);
  } catch (_) {
    return false;
  }
};

// 路由守卫通过 HttpOnly Cookie 向服务端确认会话；本地用户信息只用于界面展示。
router.beforeEach(async (to) => {
  if (!to.matched.length) return { name: 'Login' };

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  if (!requiresAuth && to.name !== 'Login') return true;
  const allowedRoles = to.matched.flatMap(record => record.meta.roles || []);

  try {
    const response = await api.getMe();
    const data = response.data || {};
    const user = data.user || data.current_user || data;
    localStorage.setItem('user', JSON.stringify(user));

    if (to.name === 'Login') return { name: 'Dashboard' };

    const role = normalizeRole(user.role || getStoredRole());
    if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
      return { name: 'Dashboard', query: { forbidden: '1' } };
    }
    return true;
  } catch (error) {
    if (error?.response?.status === 401) {
      localStorage.removeItem('user');
      if (requiresAuth) return { name: 'Login' };
      return true;
    }

    // Mobile browsers can briefly fail a session check while an app resumes
    // from the background. Keep the authenticated shell when non-sensitive
    // cached display data exists; a real server-side revocation still returns
    // 401 and is handled above and by the API interceptor.
    if (requiresAuth && hasStoredUser()) {
      const role = getStoredRole();
      if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
        return { name: 'Dashboard', query: { forbidden: '1' } };
      }
      return true;
    }
    if (requiresAuth) return { name: 'Login' };
    return true;
  }
});

export default router;
